/**
 * A/B cohort assignment for new signups (Figmafolio-to-Aero opt-in test).
 *
 * Shared by every signup path so origin (signUpProduct) and cohort (abCohort)
 * are recorded identically no matter where the account is created. Assignment
 * itself only writes fields and counters; what a cohort SEES (the onboarding
 * modal, or the forced migration screen) is decided by the callers.
 *
 * Cohort is only meaningful for figmafolio-origin signups, which are split
 * aero vs control. Aero-origin users are already on Aero, so they sit outside
 * the test and are tagged 'n/a'.
 *
 * Everything here is best-effort: buildSignupAbFields() is pure/synchronous so
 * it can be spread into the existing user-doc write without adding an await
 * that could throw before the account is created, and recordAbCohortAssignment()
 * swallows all errors so a failed stats write can never fail a signup.
 */
import firebase from '../firebase';
import { isAeroDomain } from './domainUtils';

// The four buckets of the nested "Figmafolio is now Aero" onboarding-modal
// experiment. 'control' sees no modal; 'variantA' / 'variantB' / 'variantD'
// each see one of the three design variants. This EXTENDS the original 2-way
// (aero/control) split: old 'control' maps to the new 'control', and the old
// 'aero' eligible group is now subdivided into the three variants.
export const AB_BUCKETS = ['control', 'variantA', 'variantB', 'variantD'];

// The three buckets that see a modal (everything except control). Used by the
// trigger to decide whether to show the onboarding modal at all.
export const AB_MODAL_BUCKETS = ['variantA', 'variantB', 'variantD'];

// Map a bucket to the single design variant letter it should render.
export const AB_BUCKET_VARIANT = {
  variantA: 'A',
  variantB: 'B',
  variantD: 'D',
};

// Firestore counters doc the admin funnel reads.
export const AB_STATS_COLLECTION = 'stats';
export const AB_STATS_DOC = 'abtest';

// Admin-managed on/off config for the onboarding test. Same collection and the
// same client-side admin gating as adminConfig/excludedEmails (the existing
// adminConfig/{docId} Firestore rule already covers this doc, so no new rule is
// needed). The doc shape:
//   { enabled: bool, variantA: bool, variantB: bool, variantD: bool,
//     forcedEnabled: bool, enabledAt: ISO string | null,
//     forcedEnabledAt: ISO string | null, updatedAt, updatedBy }
// HARD DEFAULT OFF: a missing doc, a missing `enabled`, or a failed read all
// resolve to enabled:false, so the test is genuinely off until an admin turns
// it on from the panel. `forcedEnabled` (the forced migration test) is equally
// hard OFF: false unless explicitly true.
export const AB_CONFIG_COLLECTION = 'adminConfig';
export const AB_CONFIG_DOC = 'abTest';

// The control vs modal split for figmafolio-origin signups when the test is ON.
// Single retunable knob: the fraction sent to CONTROL (no modal). The remaining
// (1 - ratio) is the MODAL half, divided evenly among the currently-enabled
// variants. 0.5 = even 50/50 control vs modal.
export const AB_CONTROL_RATIO = 0.5;

// The control vs aero split for the FORCED migration test (forcedEnabled).
// Fraction sent to CONTROL (normal Figmafolio onboarding); the rest is 'aero'
// (the non-dismissible migration screen at signup completion).
export const AB_FORCED_CONTROL_RATIO = 0.5;

// Normalize a raw config doc into the canonical shape with safe defaults.
// `enabled` is false unless explicitly true (default OFF). Each variant flag is
// true unless explicitly false (a variant is live by default once master is
// on). `forcedEnabled` (forced migration test) follows the MASTER rule, not
// the variant rule: false unless explicitly true, so a config written before
// the field existed leaves the forced test dark.
export function normalizeAbTestConfig(data) {
  const d = data || {};
  return {
    enabled: d.enabled === true,
    variantA: d.variantA !== false,
    variantB: d.variantB !== false,
    variantD: d.variantD !== false,
    forcedEnabled: d.forcedEnabled === true,
    enabledAt: d.enabledAt || null,
    forcedEnabledAt: d.forcedEnabledAt || null,
  };
}

// The hard-OFF default used before any read resolves and whenever a read fails.
export const AB_TEST_CONFIG_DEFAULT = normalizeAbTestConfig(null);

// Module-cached promise for the config read, so the four signup paths and the
// dashboard trigger share a single Firestore read per page load rather than one
// per call. Pass { force: true } to bypass the cache (e.g. after the admin
// writes a new value). Never throws: a failed read resolves to the OFF default.
let abTestConfigPromise = null;
export function loadAbTestConfig({ force = false } = {}) {
  if (force) abTestConfigPromise = null;
  if (!abTestConfigPromise) {
    abTestConfigPromise = firebase
      .firestore()
      .collection(AB_CONFIG_COLLECTION)
      .doc(AB_CONFIG_DOC)
      .get()
      .then((snap) => normalizeAbTestConfig(snap.exists ? snap.data() : null))
      .catch((err) => {
        console.warn('[abCohort] abTest config read failed, defaulting OFF:', err && err.message);
        return AB_TEST_CONFIG_DEFAULT;
      });
  }
  return abTestConfigPromise;
}

// Where "Try Aero" sends the user (their choice, never a forced redirect). The
// cross-origin auth handoff (landing them already-logged-in on Aero) is a
// separate future build; for now this just navigates and the click is tracked.
export const AERO_APP_URL = 'https://aeroapp.ai';

// The only entry point that enters the test: the main Figmafolio web signup
// (MainAuth's SignupPage). Plugin-driven signups (/plugin-auth, /ut-auth),
// the Aero login/signup aliases and the inline Aero auth create accounts
// through the same shared helpers but pass no entry point, so they are
// tagged with signUpProduct only and never receive a cohort or touch the
// funnel counters. Host-based assignment alone could not tell these apart
// (a plugin signup on figmafolio.com looked identical to a web signup).
export const AB_ENTRY_POINT_FIGMAFOLIO_WEB = 'figmafolio-web';

// Internal/test emails that must never enter the test, so internal signups
// stop incrementing the live counters. Seed list is a deliberate copy of
// ADMIN_EXCLUDED_EMAILS in functions/planMapping.js (functions/ cannot be
// imported from src/); the admin-managed adminConfig/excludedEmails doc is
// merged in at load time, mirroring how the rollup builds its exclusion set.
const EXCLUDED_SIGNUP_EMAILS_SEED = [
  'duanehar@outlook.com',
  'francisgonzales.claude@gmail.com',
  'deepcultureweb@gmail.com',
  'support@figmafolio.com',
  'service@puppeeland.com',
];

// Module-cached promise for the merged exclusion set (seed +
// adminConfig/excludedEmails), lowercased. Read AFTER account creation (the
// adminConfig rule requires an authenticated reader). Never throws: a failed
// read resolves to the seed alone, so signup can never break on this.
let excludedSignupEmailsPromise = null;
export function loadExcludedSignupEmails({ force = false } = {}) {
  if (force) excludedSignupEmailsPromise = null;
  if (!excludedSignupEmailsPromise) {
    excludedSignupEmailsPromise = firebase
      .firestore()
      .collection(AB_CONFIG_COLLECTION)
      .doc('excludedEmails')
      .get()
      .then((snap) => {
        const merged = new Set(EXCLUDED_SIGNUP_EMAILS_SEED.map((e) => e.toLowerCase()));
        const list = snap.exists ? snap.data().emails : null;
        if (Array.isArray(list)) {
          list.forEach((e) => {
            if (typeof e === 'string' && e.trim()) merged.add(e.trim().toLowerCase());
          });
        }
        return merged;
      })
      .catch((err) => {
        console.warn('[abCohort] excludedEmails read failed, using seed only:', err && err.message);
        return new Set(EXCLUDED_SIGNUP_EMAILS_SEED.map((e) => e.toLowerCase()));
      });
  }
  return excludedSignupEmailsPromise;
}

// Derive the product a signup originated on from the host. Uses the shared
// isAeroDomain() so every path classifies origin the same way.
export function getSignUpProduct() {
  return isAeroDomain() ? 'aero' : 'figmafolio';
}

// Assign a cohort for a new signup, gated by the admin on/off config.
//
//   - FORCED migration test ON (config.forcedEnabled === true): its own
//     master, checked FIRST and independent of the legacy `enabled` flag.
//     Figmafolio-origin signups split 50/50 (AB_FORCED_CONTROL_RATIO) between
//     'control' (normal Figmafolio onboarding) and 'aero' (the forced
//     migration screen), replacing the modal-variant split entirely.
//     Aero-origin signups stay 'n/a' as before.
//   - Forced OFF, master OFF (config.enabled !== true): returns null for EVERY
//     signup. The caller stores no cohort and counts nothing, so the signup
//     never enters the test. This is the hard "off means off" requirement.
//   - Forced OFF, master ON, aero-origin: 'n/a' (already on Aero, outside the
//     test).
//   - Forced OFF, master ON, figmafolio-origin: a 50/50 (AB_CONTROL_RATIO)
//     split between 'control' (no modal) and the MODAL half. The modal half is
//     divided evenly among only the currently-enabled variants, so disabling a
//     losing variant reallocates its share to the rest. If master is on but NO
//     variant is enabled, there is no modal to show, so the signup falls back
//     to control (control-only mode).
//
// Pass the normalized config from loadAbTestConfig(). The assignment is random
// per signup and persisted on the user doc, so a user always keeps their bucket.
export function assignAbCohort(signUpProduct, config) {
  const cfg = config || AB_TEST_CONFIG_DEFAULT;
  // Forced migration test: hard OFF unless explicitly true (a config doc
  // written before the field existed changes nothing anywhere).
  if (cfg.forcedEnabled === true) {
    if (signUpProduct !== 'figmafolio') {
      return 'n/a';
    }
    return Math.random() < AB_FORCED_CONTROL_RATIO ? 'control' : 'aero';
  }
  if (!cfg.enabled) {
    return null;
  }
  if (signUpProduct !== 'figmafolio') {
    return 'n/a';
  }
  const roll = Math.random();
  if (roll < AB_CONTROL_RATIO) {
    return 'control';
  }
  // Modal half: split evenly across the enabled modal variants only.
  const enabled = AB_MODAL_BUCKETS.filter((bucket) => cfg[bucket] !== false);
  if (enabled.length === 0) {
    return 'control';
  }
  const i = Math.floor(Math.random() * enabled.length);
  return enabled[i];
}

// Build the fields to merge into the user doc created at signup. Pass the
// normalized config from loadAbTestConfig(). When the test is OFF (or the
// signup is otherwise not in the test) assignAbCohort returns null and NO
// abCohort/abAssignedAt are written, so the user is genuinely outside the test
// and the dashboard trigger never fires. signUpProduct is always recorded
// (harmless origin tag), independent of the test being on.
//
// options gates ENTRY into the test:
//   entryPoint     - only AB_ENTRY_POINT_FIGMAFOLIO_WEB assigns a cohort;
//                    callers that pass nothing (plugin auth, Aero auth) tag
//                    signUpProduct only.
//   email          - the new account's email, matched (case-insensitively)
//                    against excludedEmails.
//   excludedEmails - the Set from loadExcludedSignupEmails(); an excluded
//                    email gets no cohort, so internal signups never touch
//                    the live counters.
export function buildSignupAbFields(signUpProduct, config, options = {}) {
  const { entryPoint = null, email = null, excludedEmails = null } = options;
  const product = signUpProduct || getSignUpProduct();
  const fields = { signUpProduct: product };
  if (entryPoint !== AB_ENTRY_POINT_FIGMAFOLIO_WEB) {
    return fields;
  }
  if (
    excludedEmails &&
    typeof email === 'string' &&
    excludedEmails.has(email.trim().toLowerCase())
  ) {
    return fields;
  }
  const abCohort = assignAbCohort(product, config);
  if (abCohort) {
    fields.abCohort = abCohort;
    fields.abAssignedAt = new Date().toISOString();
  }
  return fields;
}

// Best-effort, fire-and-forget-safe counter increment. NEVER throws: any
// failure is logged and swallowed so signup always succeeds even when the
// stats write fails. Pass the same fields written to the user doc so the
// counter matches the recorded cohort exactly.
export async function recordAbCohortAssignment({ signUpProduct, abCohort } = {}) {
  // No cohort means the test was OFF (or the signup is outside it): count
  // nothing. "Off" must be genuinely off, not assign-but-hide, so a signup that
  // never got a cohort never touches the funnel counters.
  if (!abCohort) return;
  try {
    const db = firebase.firestore();
    const inc = firebase.firestore.FieldValue.increment(1);
    const updates = {};
    if (signUpProduct === 'figmafolio') {
      updates.figmafolioTotal = inc;
      // Per-bucket signup counter (control / variantA / variantB / variantD).
      // Key shape figmafolio<Bucket>Cohort keeps the existing
      // figmafolioControlCohort name and adds the three variant counters.
      const bucketKey =
        'figmafolio' + (abCohort || 'control').charAt(0).toUpperCase() +
        (abCohort || 'control').slice(1) + 'Cohort';
      updates[bucketKey] = inc;
    } else {
      updates.aeroOriginSignups = inc;
    }
    await db
      .collection(AB_STATS_COLLECTION)
      .doc(AB_STATS_DOC)
      .set(updates, { merge: true });
  } catch (err) {
    // Stats are non-critical; never surface to the signup flow.
    console.warn('[abCohort] counter increment failed (non-blocking):', err && err.message);
  }
}

// ===================================================================
// AERO ONBOARDING MODAL (the A/B/D nested experiment)
// ===================================================================
//
// A dismissible modal shown ONCE to a brand-new figmafolio-origin signup placed
// in one of the three modal buckets (variantA/B/D), immediately after signup,
// over the dashboard. 'control' sees nothing. It introduces Aero and invites a
// no-pressure "Try Aero". All tracking extends the same stats/abtest counters
// the admin funnel already reads, carrying the variant.

// True when this user should see the onboarding modal: a figmafolio-origin
// account in a modal bucket that has not yet seen/dismissed it. Pass the user
// doc data (or any object carrying signUpProduct, abCohort, aeroOnboardingSeen).
export function shouldShowAeroOnboarding(userData = {}, config) {
  const cfg = config || AB_TEST_CONFIG_DEFAULT;
  // Master off -> show to no one, even users assigned a bucket while it was on.
  if (!cfg.enabled) return false;
  const { signUpProduct, abCohort, aeroOnboardingSeen } = userData;
  if (
    signUpProduct !== 'figmafolio' ||
    !AB_MODAL_BUCKETS.includes(abCohort) ||
    aeroOnboardingSeen
  ) {
    return false;
  }
  // Per-variant kill switch: if this user's assigned variant is now disabled,
  // stop showing it (its future share already went to the enabled variants).
  return cfg[abCohort] !== false;
}

// The design variant letter ('A' | 'B' | 'D') for a bucket, or null if the
// bucket sees no modal (control / n/a / unknown).
export function variantForCohort(abCohort) {
  return AB_BUCKET_VARIANT[abCohort] || null;
}

// Mark the modal as seen on the user doc so it never reappears. Best-effort;
// never throws (a failed write just means it may show again, not a broken app).
export async function markAeroOnboardingSeen(uid) {
  if (!uid) return;
  try {
    await firebase
      .firestore()
      .collection('user')
      .doc(uid)
      .set(
        {
          aeroOnboardingSeen: true,
          aeroOnboardingSeenAt: new Date().toISOString(),
        },
        { merge: true }
      );
  } catch (err) {
    console.warn('[abCohort] aeroOnboardingSeen write failed (non-blocking):', err && err.message);
  }
}

// Stamp "this user clicked Try Aero" on the user doc, alongside the
// aggregate try counter below. The rollup's switched segment (modal users
// who tried Aero, and of those who published on Aero / returned) needs a
// PER-USER marker; the aeroOnboarding_*_try counters are blind aggregates.
// Forward-only: users who tried before this stamp existed are not
// identifiable. Best-effort; never throws.
export async function markAeroOnboardingTried(uid) {
  if (!uid) return;
  try {
    await firebase
      .firestore()
      .collection('user')
      .doc(uid)
      .set(
        {
          aeroOnboardingTried: true,
          aeroOnboardingTriedAt: new Date().toISOString(),
        },
        { merge: true }
      );
  } catch (err) {
    console.warn('[abCohort] aeroOnboardingTried write failed (non-blocking):', err && err.message);
  }
}

// Record an onboarding event on stats/abtest, carrying the variant so the admin
// can aggregate per bucket. event is 'shown' | 'try' | 'dismiss'; variant is
// 'A' | 'B' | 'D'; source (for dismiss) is 'button' | 'close' | 'overlay'.
// Counter key shape: aeroOnboarding_<variant>_<event> (e.g.
// aeroOnboarding_A_shown), plus a per-variant dismiss-source breakdown
// (aeroOnboarding_A_dismiss_overlay). Best-effort; never throws.
export async function recordAeroOnboardingEvent(event, variant, source) {
  if (!variant) return;
  try {
    const db = firebase.firestore();
    const inc = firebase.firestore.FieldValue.increment(1);
    const updates = {
      [`aeroOnboarding_${variant}_${event}`]: inc,
    };
    if (event === 'dismiss' && source) {
      updates[`aeroOnboarding_${variant}_dismiss_${source}`] = inc;
    }
    await db
      .collection(AB_STATS_COLLECTION)
      .doc(AB_STATS_DOC)
      .set(updates, { merge: true });
  } catch (err) {
    console.warn('[abCohort] onboarding event write failed (non-blocking):', err && err.message);
  }
}
